import { FC, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import BaseModal from '@app/shared/components/modal/BaseModal';
import { ModalSize } from '@app/shared/components/modal/base-modal-enums';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { uiValuesSlice } from '@core/store/ui-values-slice';
import { EquityItem } from '../../../CapitalStructureItemFormErf';
import {
  CalcMethod,
  EquityRefinancingMovementType,
  EventKey,
  InstrumentType,
  StructureDimension,
} from '@app/shared/models/contracts/enums/shared-enums';
import { useParams } from 'react-router-dom';
import Sidebar, { SidebarVariation } from '@app/shared/components/sidebar/Sidebar';
import ExclamationTriangleSvg from '@app/shared/icons/legacy/ExclamationTriangleSvg';
import { ProjectDto } from '@app/shared/models/contracts/project-dto';
import styles from '../capital-structure.module.scss';
import InstrumentTileErf from '../InstrumentTileErf';
import { MAX_INSTRUMENT_COUNT } from '@app/shared/constants/capital-structure';
import useIsReadOnly from '@app/core/hooks/customUseIsReadOnly';
import { FormProvider } from 'react-hook-form';
import FormField from '@app/shared/components/form-controls/form-field/FormField';
import { textFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import {
  maxLengthValidator,
  getRequiredValidator,
  uniqueEventNarrative,
  dateBetweenValidator,
} from '@app/core/validations/hook-forms/validators';
import { calculateBuildStructure } from '@app/core/store/capital-structure-slice';
import { useDrop } from 'react-dnd';
import {
  cloneToMutableData,
  getNewInstrument,
  getProjectDraftWithAddedInstrument,
  getProjectDraftWithInstrumentAddedToEvent,
} from '@app/shared/helpers';
import {
  getAllInstrumentsFromBuildStructure,
  getEventInstrumentsFromEvent,
  selectCapitalStructure,
} from '@app/core/store/capital-structure-slice-selectors';
import { CapitalStructureEventDto } from '@app/shared/models/contracts/capital-structure-debt-instrument-dto';
import classNames from 'classnames';
import FormDatePicker from '@app/shared/components/form-controls/form-date-picker/FormDatePicker';
import { EventUpdateState } from '../CapitalStructureErf';
import { deepEqualIgnoreOrder } from '@app/shared/helpers/clone/compare-deep-normalised';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import CapitalStructureItemFormErf2 from '../../../capital-structure-item-form/CapitalStructureItemFormERF2';
import ResetEventModal from './Modals/ResetEventModal';
import DateChangeModal from './Modals/DateChangeModal';
import {
  areCouponsEqual,
  getEquityItem,
  getOpeningCoupon,
  getCurrentCoupon,
  previousEventHasErrors,
} from '../utils/editEventHelper';
import { useEditEventForm } from '../utils/hooks/useEditEventForm';
import CapitalStructureFaults from '../CapitalStructureFaults';
import { useCapitalStructureFaults } from '../utils/hooks/useCapitalStructureFaults';
import { useEventDateBoundaries } from '../utils/hooks/useEventDateBoundaries';

interface CopyEventModalProps {
  setIsOpen: (value: boolean) => void;
  isOpen: boolean;
  buildStructureEvent: CapitalStructureEventDto;
  instrumentId?: string;
  modalRanks: JSX.Element[];
  openingEventState: EventUpdateState | null;
  openingProjectState: ProjectDto;
  currentEventState: EventUpdateState | null;
  setOpeningProjectState: (project: ProjectDto) => void;
  setOpeningEventState: (event: EventUpdateState) => void;
}

const PARENT_CLASSNAME = 'capital-structure';

export const EditEventModal: FC<CopyEventModalProps> = ({
  isOpen,
  setIsOpen,
  buildStructureEvent,
  instrumentId,
  modalRanks,
  openingEventState,
  openingProjectState,
  currentEventState,
  setOpeningProjectState,
  setOpeningEventState,
}): JSX.Element | null => {
  const { l } = useLocale();
  const { caseId } = useParams();
  const dispatch = useAppDispatch();
  const isReadOnly = useIsReadOnly();
  const { maxRefinancingEventNameLength: maxEventNameLength, maxCapitalStructureYears } =
    useAppSelector((state) => state.uiValues);
  const { projectDraft: project, isBuildStructureValid } = useAppSelector((state) => state.project);
  const isOpmOnly = project.details.calcMethod === CalcMethod.OPM;
  const capitalStructures = project.capitalStructures;
  const capitalStructureKey = Object.keys(capitalStructures)?.[0];
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const buildStructures = useAppSelector((state) => state.capitalStructure.values.buildStructures);
  const buildStructure = buildStructures[capitalStructureKey];

  const events = capitalStructure.events;
  const eventNarrative = events[buildStructureEvent.id]?.narrative;
  const eventDate = events[buildStructureEvent.id]?.eventDate;
  const existingEvents = isOpmOnly
    ? Object.fromEntries(
        capitalStructure.eventSets[EventKey.OpmOnly]?.events.map((eventId) => [
          eventId,
          capitalStructure.events[eventId],
        ]) ?? []
      )
    : Object.fromEntries(
        Object.entries(events).filter(
          ([eventId, _]) => !capitalStructure.eventSets[EventKey.OpmOnly]?.events.includes(eventId)
        )
      );
  const nonSelectedEvents = Object.fromEntries(
    Object.entries(existingEvents).filter(([key, _]) => key !== buildStructureEvent.id)
  );
  const instrumentDefinitions = buildStructures?.[capitalStructureKey]?.instrumentDefinitions;
  const eventInstruments = getEventInstrumentsFromEvent(buildStructureEvent, instrumentDefinitions);

  const {
    formMethods,
    formSubmitHandler,
    cancelDateChange,
    showDateChangeModal,
    confirmDateChange,
    hasHeaderChanged,
  } = useEditEventForm(
    eventNarrative,
    eventDate,
    project,
    capitalStructure,
    buildStructure,
    capitalStructureKey,
    buildStructureEvent.id
  );

  const allInstruments = useMemo(
    () => getAllInstrumentsFromBuildStructure(buildStructures),
    [buildStructures]
  );

  const isEvent = !(buildStructureEvent.id === EventKey.InitialCapitalStructure);
  const eventSetId = isOpmOnly
    ? EventKey.OpmOnly
    : project.pwermInput.cases.find((caseItem) => caseItem.caseId === caseId)?.eventSetId ??
      EventKey.EmptyEventSet;
  const selectedEvent = buildStructure.eventSets[eventSetId]?.events.find(
    (event) => event.id === buildStructureEvent.id
  );

  const [, drop] = useDrop(
    () => ({
      accept: [
        InstrumentType.ShareholderLoanNotes,
        InstrumentType.PreferredShares,
        InstrumentType.OrdinaryEquity,
      ],
      drop(_item: string, monitor) {
        const draggedInstrumentType = monitor.getItemType() as InstrumentType;
        addInstrument(draggedInstrumentType, buildStructureEvent.id);
      },
      collect: (monitor) => ({
        isActive: monitor.canDrop() && monitor.isOver(),
      }),
    }),
    [project, eventInstruments]
  );

  const { picker, validator } = useEventDateBoundaries({
    eventId: buildStructureEvent.id,
    buildStructure,
    investmentDate: project.investmentDate,
    yearEndDate: project.yearEndDate,
    maxCapitalStructureYears,
  });

  const addInstrument = async (instrumentType: InstrumentType, eventId: string): Promise<void> => {
    const newInstrumentId = uuidv4();
    const newInstrument = getNewInstrument(
      project,
      allInstruments,
      instrumentType,
      newInstrumentId,
      eventDate
    );

    const updateProjectDraft =
      eventId !== EventKey.InitialCapitalStructure
        ? getProjectDraftWithInstrumentAddedToEvent({ draft: project, eventId, newInstrument })
        : getProjectDraftWithAddedInstrument({ draft: project, newInstrument });

    await dispatch(calculateBuildStructure({ project: updateProjectDraft, ignoreErrors: true }));

    dispatch(
      uiValuesSlice.actions.setActiveInstrument({
        instrumentId: newInstrumentId,
        eventId: buildStructureEvent.id,
      })
    );
  };

  const [isResetEventModalVisible, setResetEventModalVisible] = useState<boolean>(false);
  const [rerenderKey, setRerenderKey] = useState<number>(0);
  // Track if the reset modal was triggered by a close action
  const [shouldCloseAfterReset, setShouldCloseAfterReset] = useState<boolean>(false);

  const formValuesContainValidationErrors = Object.keys(formMethods.formState.errors).length > 0;

  const { informationFaults, warningFaults, errorFaults } = useCapitalStructureFaults(
    buildStructure,
    buildStructureEvent.id,
    eventSetId
  );

  if (!buildStructures) {
    return null;
  }

  const handleReset = async () => {
    await dispatch(calculateBuildStructure({ project: openingProjectState, ignoreErrors: true }));
    setRerenderKey((prevKey) => prevKey + 1);
    if (isEvent && instrumentId) {
      const originalEvent =
        openingProjectState.capitalStructures[capitalStructureKey].events[buildStructureEvent.id];

      formMethods.reset({
        narrative: originalEvent.narrative,
        eventDate: originalEvent.eventDate,
        movements: {
          [instrumentId]: {
            coupon: {
              value: undefined,
              date: originalEvent.eventDate,
              type: undefined,
              cashPayProportion: undefined,
            },
          },
        },
      });
    }
    setResetEventModalVisible(false);
    if (shouldCloseAfterReset) {
      setIsOpen(false);
      setShouldCloseAfterReset(false);
    }
  };

  const equityItem: EquityItem | null = instrumentId
    ? getEquityItem(instrumentId, capitalStructure, project, isEvent, selectedEvent)
    : null;

  const handleModalClose = () => {
    if (!isInitialEqualToCurrent || hasHeaderChanged) {
      setShouldCloseAfterReset(true);
      setResetEventModalVisible(true);
      return;
    }
    setIsOpen(false);

    if (buildStructureEvent.id !== EventKey.InitialCapitalStructure) {
      const currentEventMovements = cloneToMutableData(
        events[buildStructureEvent.id]?.movements ?? {}
      );

      // Remove empty value adjustment movements where the ownership split has been updated but without any owner/pro-rata movements
      Object.keys(currentEventMovements).forEach((key) => {
        const movement = currentEventMovements[key];
        if (
          movement.ownershipSplit &&
          movement.movementType === EquityRefinancingMovementType.ValueAdjustment &&
          movement.ownerMovements &&
          Object.keys(movement.ownerMovements).length === 0 &&
          Object.keys(movement).length === 3
        ) {
          delete currentEventMovements[key];
        }
      });

      const updatedProjectDraft = {
        ...project,
        capitalStructures: {
          ...project.capitalStructures,
          [capitalStructureKey]: {
            ...capitalStructure,
            events: {
              ...capitalStructure.events,
              [buildStructureEvent.id]: {
                ...capitalStructure.events[buildStructureEvent.id],
                movements: currentEventMovements,
                narrative: !formMethods.formState.errors.narrative
                  ? formMethods.getValues().narrative
                  : eventNarrative,
                eventDate: !formMethods.formState.errors.eventDate
                  ? formMethods.getValues().eventDate
                  : eventDate,
              },
            },
          },
        },
      };

      dispatch(calculateBuildStructure({ project: updatedProjectDraft, ignoreErrors: true }));
    }

    dispatch(
      uiValuesSlice.actions.setActiveInstrument({
        instrumentId: undefined,
        eventId: undefined,
      })
    );
  };

  const handleApply = () => {
    if (isApplyDisabled) {
      return;
    }

    setOpeningEventState(currentEventState);
    setOpeningProjectState(project);
    setIsOpen(false);
  };

  const hasErrors = previousEventHasErrors(selectedEvent, buildStructure, eventSetId);

  const haveHitMaxInstruments = Object.keys(instrumentDefinitions).length >= MAX_INSTRUMENT_COUNT;
  const shouldDisableTiles = haveHitMaxInstruments || isReadOnly || hasErrors;
  const shouldDisableHeaderFields = isReadOnly || hasErrors;

  const openingCoupon = getOpeningCoupon(
    buildStructureEvent.id,
    capitalStructureKey,
    instrumentId,
    openingProjectState
  );

  const currentCoupon = getCurrentCoupon(buildStructureEvent.id, events, instrumentId);

  const isInitialEqualToCurrent =
    deepEqualIgnoreOrder(openingEventState, currentEventState) &&
    areCouponsEqual(openingCoupon, currentCoupon);

  const isApplyDisabled =
    errorFaults.length > 0 ||
    (!hasHeaderChanged && isInitialEqualToCurrent) ||
    formValuesContainValidationErrors ||
    !isBuildStructureValid;

  return (
    <BaseModal
      size={ModalSize.Large}
      title={l('_EditEvent')}
      isOpen={isOpen}
      preventModalFullHeight
      shouldCloseOnOverlayClick={isInitialEqualToCurrent && !formValuesContainValidationErrors}
      shouldCloseOnEsc={isInitialEqualToCurrent && !formValuesContainValidationErrors}
      onRequestClose={handleModalClose}
      onCloseButtonClick={handleModalClose}>
      <div
        className={classNames(styles[`${PARENT_CLASSNAME}`], {
          [styles[`${PARENT_CLASSNAME}__event-modal-header`]]:
            buildStructureEvent.id !== EventKey.InitialCapitalStructure,
          [styles[`${PARENT_CLASSNAME}__initial-structure-modal-header`]]:
            buildStructureEvent.id === EventKey.InitialCapitalStructure,
        })}>
        {buildStructureEvent.id !== EventKey.InitialCapitalStructure && (
          <FormProvider {...formMethods}>
            <form
              className={styles[`${PARENT_CLASSNAME}__form`]}
              onBlur={formMethods.handleSubmit(formSubmitHandler)}>
              <div className={styles[`${PARENT_CLASSNAME}__event-date`]}>
                <FormDatePicker
                  name="eventDate"
                  label={l('_EventDate')}
                  placeholder={l('_EventDate')}
                  minDate={picker.minInclusiveDate}
                  maxDate={picker.maxInclusiveDate}
                  disabled={shouldDisableHeaderFields}
                  inlineLabel
                  isModalHeaderField
                  rules={{
                    ...getRequiredValidator(),
                    validate: {
                      dateMustBeWithinRange:
                        validator.minExclusiveDate || validator.maxExclusiveDate
                          ? dateBetweenValidator(
                              validator.minExclusiveDate,
                              validator.maxExclusiveDate
                            ).validate
                          : () => true,
                    },
                  }}
                />
              </div>
              <div className={styles[`${PARENT_CLASSNAME}__event-narrative`]}>
                <FormField
                  name="narrative"
                  label={l('_EventNarrativeLowercase')}
                  placeholder={l('_Narrative')}
                  disabled={shouldDisableHeaderFields}
                  inlineLabel
                  isModalHeaderField
                  {...textFieldFormattingProps}
                  rules={{
                    ...getRequiredValidator(),
                    ...uniqueEventNarrative(nonSelectedEvents),
                    ...maxLengthValidator(maxEventNameLength),
                  }}
                />
              </div>
            </form>
          </FormProvider>
        )}
        <div className={styles[`${PARENT_CLASSNAME}__controls`]}>
          <div className={styles[`${PARENT_CLASSNAME}__errors`]}>
            <CapitalStructureFaults
              informationFaults={informationFaults}
              warningFaults={warningFaults}
              errorFaults={errorFaults}
              instrumentDefinitions={instrumentDefinitions}
              isWithinEditEventModal
            />
          </div>
          <Button
            appearance={ButtonAppearance.DEFAULT_SECONDARY}
            size={ButtonSize.SMALL}
            onClick={() => {
              setResetEventModalVisible(true);
              setShouldCloseAfterReset(false);
            }}
            disabled={!hasHeaderChanged && isInitialEqualToCurrent}>
            {l('_Reset')}
          </Button>
          <Button
            appearance={ButtonAppearance.DEFAULT_PRIMARY}
            size={ButtonSize.SMALL}
            onClick={handleApply}
            disabled={isApplyDisabled}>
            {l('_Apply')}
          </Button>
        </div>
      </div>
      <div className={styles[`${PARENT_CLASSNAME}__event-modal-instruments`]}>
        <div className={styles[`${PARENT_CLASSNAME}__event-build`]}>
          <div className={styles[`${PARENT_CLASSNAME}__actions`]}>
            {Object.entries(InstrumentType).map(([key, value]) => (
              <InstrumentTileErf
                key={key}
                instrumentType={value}
                isDisabled={shouldDisableTiles}
                isErfModal
                haveHitMaxInstruments={haveHitMaxInstruments}
              />
            ))}
          </div>
          <div
            ref={drop}
            style={{
              width:
                instrumentId && equityItem
                  ? StructureDimension.ModalWidth
                  : StructureDimension.ModalWithoutFormWidth,
            }}
            className={
              instrumentId && equityItem
                ? styles[`${PARENT_CLASSNAME}__event-structure`]
                : styles[`${PARENT_CLASSNAME}__event-structure-without-form`]
            }>
            {modalRanks.map((rank) => {
              return rank;
            })}
          </div>
        </div>
        {hasErrors ? (
          <Sidebar
            sidebarVariation={SidebarVariation.Empty}
            data-testid="instrument-details-sidebar"
            isErfModal>
            <ExclamationTriangleSvg className={styles[`${PARENT_CLASSNAME}__icon`]} />
            <span>{l('_EventsWithErrors')}</span>
          </Sidebar>
        ) : (
          instrumentId &&
          equityItem && (
            <CapitalStructureItemFormErf2
              key={instrumentId + rerenderKey}
              equityItem={equityItem}
              eventSetId={eventSetId}
              warningFaults={warningFaults}
            />
          )
        )}
      </div>
      <ResetEventModal
        visible={isResetEventModalVisible}
        setVisible={setResetEventModalVisible}
        handleReset={handleReset}
      />
      <DateChangeModal
        visible={showDateChangeModal}
        onConfirm={() => confirmDateChange()}
        onCancel={() => cancelDateChange()}
      />
    </BaseModal>
  );
};
